(() => {
  if (customElements.get('orbea-configurator')) return;

  const GROUP_LABELS = {
    crankset: 'Crankstel',
    handlebar: 'Stuur',
    rotors: 'Remschijven',
    wheels: 'Wielset',
    wheelset: 'Wielset',
    cassette: 'Cassette',
    saddle: 'Zadel',
    seatpost: 'Zadelpen',
    stem: 'Stuurpen',
    tyres: 'Banden',
    brakes: 'Remmen',
    fork: 'Voorvork',
    tools: 'Gereedschap',
    mudguard: 'Spatborden',
    'front wheel axle': 'Vooras',
    'rear wheel axle': 'Achteras',
  };

  const groupLabel = (group) => {
    if (String(group?.name || '').toLowerCase() === 'fork'
      && (group.options || []).some((option) => /\bshock\b/i.test(String(option.name || '')))) {
      return 'Voorvork en achterdemper';
    }
    return GROUP_LABELS[String(group?.name || '').toLowerCase()] || group?.name || '';
  };

  const MEASUREMENT_LABELS = {
    crank: 'Cranklengte',
    handlebar: 'Stuurbreedte',
    'integrated handlebar': 'Maat cockpit',
    stem: 'Lengte stuurpen',
    'dropper seatpost': 'Veerweg dropper',
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const parseJson = (root, selector, fallback) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.error(`Orbea-configuratie kon niet worden gelezen: ${selector}`, error);
      return fallback;
    }
  };

  const parseJsonValue = (value, fallback) => {
    try {
      return JSON.parse(String(value || ''));
    } catch (error) {
      return fallback;
    }
  };

  const safeImageUrl = (value) => {
    try {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const url = new URL(raw, window.location.origin);
      if (url.protocol !== 'https:') return '';
      if (url.origin !== window.location.origin
        && !/(^|\.)orbea\.com$/i.test(url.hostname)
        && !/(^|\.)shopifycdn\.com$/i.test(url.hostname)
        && !/(^|\.)shopify\.com$/i.test(url.hostname)) return '';
      return url.href;
    } catch (error) {
      return '';
    }
  };

  const safeSwatchBackground = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^#[0-9a-f]{3,8}$/i.test(raw) || /^(?:rgb|rgba|hsl|hsla)\([\d\s.,%+-]+\)$/i.test(raw)) return raw;
    const urlMatch = raw.match(/^url\((['"]?)(.*?)\1\)$/i);
    if (!urlMatch) return '';
    const url = safeImageUrl(urlMatch[2]);
    return url ? `url("${url.replaceAll('"', '%22')}")` : '';
  };

  const safeOrbeaUrl = (value) => {
    try {
      const url = new URL(String(value || ''));
      if (url.protocol !== 'https:' || !/(^|\.)orbea\.com$/i.test(url.hostname)) return null;
      return url;
    } catch (error) {
      return null;
    }
  };

  const parseOrbeaCustomization = (value, fallbackUrl) => {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const pastedUrl = safeOrbeaUrl(raw);
    const code = String(pastedUrl?.searchParams.get('configuration') || raw).trim();
    if (!/^[A-Za-z0-9]{10}$/.test(code)) return null;
    const url = pastedUrl || safeOrbeaUrl(fallbackUrl) || new URL('https://www.orbea.com/en-nl/');
    url.searchParams.set('configuration', code);
    return { code, url: url.href };
  };

  const safeImageCrop = (value) => {
    const values = [value?.x, value?.y, value?.width, value?.height].map(Number);
    if (!values.every(Number.isFinite)) return null;
    const [x, y, width, height] = values;
    if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 1.001 || y + height > 1.001) return null;
    return values;
  };

  const optionDescription = (group, option) => {
    const groupName = String(group?.name || '').trim().toLowerCase();
    if (groupName === 'crankset') return '';
    if (groupName === 'handlebar') return '';
    if (groupName === 'rotors') return 'Kies de gewenste diameter van de voor- en achterremschijf.';
    if (groupName === 'wheels' || groupName === 'wheelset') return '';
    if (groupName === 'cassette') return '';
    if (['tyres', 'brakes', 'fork', 'tools', 'mudguard', 'seatpost', 'front wheel axle', 'rear wheel axle'].includes(groupName)) return '';
    return String(option?.description || '').trim();
  };

  const cassettePresentation = (option) => {
    const name = String(option?.name || '').trim();
    const ratioMatch = name.match(/\b(\d{1,2})\s*[-–]\s*(\d{1,2})T\b/i);
    if (!ratioMatch) return null;
    const speedMatch = name.match(/\b(\d{1,2})\s*S(?:PEED)?\b/i);
    const type = name
      .replace(ratioMatch[0], '')
      .replace(speedMatch?.[0] || '', '')
      .replace(/\s+/g, ' ')
      .trim();
    return {
      title: `${ratioMatch[1]}-${ratioMatch[2]}T`,
      details: [type, speedMatch ? `${speedMatch[1]}-speed` : ''].filter(Boolean),
    };
  };

  const wheelPresentation = (option) => {
    const name = String(option?.name || '').trim();
    const description = String(option?.description || '');
    const material = description.match(/(?:^|\n)Material\s*\n\s*([^\n]+)/i)?.[1] || '';
    const hasCarbonSpokes = /carbon\s+spokes/i.test(name);
    const isAluminium = /alumini?um|alloy/i.test(`${name} ${material}`)
      || /\b(?:RP35PRO|RC25PRO)\b/i.test(name);
    return {
      title: hasCarbonSpokes
        ? 'Carbon wielset met carbon spaken'
        : isAluminium ? 'Aluminum wielset' : 'Carbon wielset',
      details: [name],
    };
  };

  const mudguardPresentation = (option) => {
    const name = String(option?.name || '').trim();
    const without = !name || /^n\/?a$/i.test(name);
    return {
      title: without ? 'Zonder spatborden' : 'Met spatborden',
      details: without ? [] : [name],
    };
  };

  const toolsPresentation = (option) => {
    const name = String(option?.name || '').trim();
    const without = !name || /^n\/?a$/i.test(name);
    return { title: without ? 'Zonder multitool' : 'Met multitool', details: without ? [] : [name] };
  };

  const axlePresentation = (option) => {
    const name = String(option?.name || '').trim();
    return {
      title: /\blite\b/i.test(name) ? 'Lichte steekas' : 'Standaard steekas',
      details: [name],
    };
  };

  const saddlePresentation = (option) => {
    const name = String(option?.name || '').trim();
    const description = String(option?.description || '');
    const rail = description.match(/(?:^|\n)Rail\s*\n\s*([^\n]+)/i)?.[1]?.trim() || '';
    const railSizeMatch = rail.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*mm/i);
    const railSize = railSizeMatch ? `${railSizeMatch[1].replace(',', '.')}×${railSizeMatch[2].replace(',', '.')} mm` : '';
    const railMaterial = rail.replace(railSizeMatch?.[0] || '', '').trim();
    const dimensions = description.match(/(?:^|\n)Dimensions\s*\n\s*([^\n]+)/i)?.[1]?.trim() || '';
    const width = dimensions.match(/(\d+(?:[.,]\d+)?)\s*[x×]/i)?.[1]?.replace(',', '.') || '';
    const carbonRail = /carbon/i.test(railMaterial || rail);
    const metalRail = /kium|alloy|steel|xsilite|titan|mangan|chrom|inox|metal/i.test(railMaterial || rail);
    return {
      title: metalRail ? 'Zadel met metalen rail' : carbonRail ? 'Zadel met carbon rail' : 'Zadel',
      details: [
        name,
        railSize || railMaterial ? `Rail: ${[railSize, railMaterial].filter(Boolean).join(' · ')}` : '',
        width ? `Breedte: ${width} mm` : '',
      ].filter(Boolean),
    };
  };

  class OrbeaConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.config = parseJson(this, '[data-orbea-config]', {});
      this.priceMap = parseJson(this, '[data-orbea-price-map]', { prices: {} });
      this.shopifyProduct = parseJson(this, '[data-orbea-shopify-product]', { optionNames: [], variants: [] });
      this.stockProfiles = parseJson(this, '[data-orbea-stock-profiles]', { profiles: [] }).profiles || [];
      this.customizeUrlsBySize = parseJsonValue(this.dataset.customizeUrlsBySize, {});
      if (!Array.isArray(this.config?.groups) || !this.config.groups.length) return;

      this.hidden = false;
      this.testMode = this.dataset.testMode === 'true';
      this.openButton = this.querySelector('[data-orbea-open]');
      this.overlay = this.querySelector('[data-orbea-overlay]');
      this.dialog = this.querySelector('[role="dialog"]');
      this.closeButton = this.querySelector('[data-orbea-close]');
      this.content = this.querySelector('[data-orbea-content]');
      this.totalNode = this.querySelector('[data-orbea-total]');
      this.deltaNode = this.querySelector('[data-orbea-delta]');
      this.saveButton = this.querySelector('[data-orbea-save]');
      this.savedSummary = this.querySelector('[data-orbea-saved-summary]');
      this.fulfilmentNode = this.querySelector('[data-orbea-fulfilment]');
      this.myoLinkPanel = this.querySelector('[data-orbea-myo-link]');
      this.myoOpenLink = this.querySelector('[data-orbea-myo-open]');
      this.myoInput = this.querySelector('[data-orbea-myo-input]');
      this.myoAttachButton = this.querySelector('[data-orbea-myo-attach]');
      this.myoStatus = this.querySelector('[data-orbea-myo-status]');
      this.productInfo = this.closest('product-info') || this.closest('[id^="MainProduct-"]') || document;
      this.selectedOptions = {};
      this.selectedMeasurements = {};
      this.selectedWheelColors = {};
      this.componentChoiceDirty = false;
      this.myoDesignsByVariant = new Map();
      this.activeVariantId = '';
      this.activeSize = '';
      this.stockImageRestore = [];
      this.priceRestore = null;
      this.inventoryRestoreByVariant = new Map();
      this.syncTimers = [];
      this.imageCropObserver = null;

      this.onOpen = () => this.open();
      this.onClose = () => this.close();
      this.onSave = () => this.save();
      this.onOverlayClick = (event) => {
        if (event.target === this.overlay) this.close();
      };
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onChange = (event) => this.handleChange(event);
      this.onMyoAttach = () => this.attachMyoDesign();
      this.onMyoInputKeydown = (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.attachMyoDesign();
      };
      this.onPageChange = (event) => {
        if (this.contains(event.target)) return;
        this.scheduleVariantSync();
      };

      this.openButton?.addEventListener('click', this.onOpen);
      this.closeButton?.addEventListener('click', this.onClose);
      this.saveButton?.addEventListener('click', this.onSave);
      this.overlay?.addEventListener('click', this.onOverlayClick);
      this.content?.addEventListener('change', this.onChange);
      this.myoAttachButton?.addEventListener('click', this.onMyoAttach);
      this.myoInput?.addEventListener('keydown', this.onMyoInputKeydown);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('change', this.onPageChange, true);
      if (this.overlay && this.overlay.parentElement !== document.body) document.body.append(this.overlay);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, () => this.scheduleVariantSync());
      }

      this.syncVariant(true);
      this.scheduleVariantSync();
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.onOpen);
      this.closeButton?.removeEventListener('click', this.onClose);
      this.saveButton?.removeEventListener('click', this.onSave);
      this.overlay?.removeEventListener('click', this.onOverlayClick);
      this.content?.removeEventListener('change', this.onChange);
      this.myoAttachButton?.removeEventListener('click', this.onMyoAttach);
      this.myoInput?.removeEventListener('keydown', this.onMyoInputKeydown);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('change', this.onPageChange, true);
      this.unsubscribeVariant?.();
      this.imageCropObserver?.disconnect();
      this.syncTimers.forEach((timer) => window.clearTimeout(timer));
      this.restoreStockImage();
      this.restoreMainPrice();
      this.overlay?.remove();
    }

    productForm() {
      return this.dataset.productFormId ? document.getElementById(this.dataset.productFormId)?.closest('product-form') : null;
    }

    variantIdFromPage() {
      const formValue = this.productForm()?.querySelector('[name="id"]')?.value;
      if (formValue) return String(formValue);
      const urlValue = new URL(window.location.href).searchParams.get('variant');
      return String(urlValue || this.dataset.selectedVariantId || '');
    }

    currentVariant() {
      const variantId = this.variantIdFromPage();
      return (this.shopifyProduct.variants || []).find((variant) => String(variant.id) === variantId)
        || (this.shopifyProduct.variants || []).find((variant) => String(variant.id).endsWith(`/${variantId}`))
        || null;
    }

    optionIndex(pattern) {
      return (this.shopifyProduct.optionNames || []).findIndex((name) => pattern.test(String(name || '')));
    }

    currentSize() {
      const variant = this.currentVariant();
      const index = this.optionIndex(/maat|size/i);
      return index >= 0 ? String(variant?.options?.[index] || '') : '';
    }

    currentColor() {
      const variant = this.currentVariant();
      const index = this.optionIndex(/kleur|color|colour/i);
      return index >= 0 ? String(variant?.options?.[index] || '') : '';
    }

    pageOptionInputs(index) {
      const optionName = String(this.shopifyProduct.optionNames?.[index] || '').trim().toLowerCase();
      if (!optionName) return [];
      return Array.from(this.productInfo?.querySelectorAll?.('input[type="radio"]') || []).filter((input) => {
        const inputName = String(input.name || '').trim().toLowerCase();
        return inputName === optionName || inputName.startsWith(`${optionName}-`);
      });
    }

    productOptionValues(index) {
      const values = [];
      const seen = new Set();
      for (const input of this.pageOptionInputs(index)) {
        const value = String(input.value || '').trim();
        if (!value || seen.has(value)) continue;
        const label = input.id ? this.productInfo.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
        const swatch = label?.querySelector('.swatch');
        values.push({
          value,
          swatchBackground: safeSwatchBackground(swatch?.style.getPropertyValue('--swatch--background')),
          disabled: Boolean(input.disabled),
        });
        seen.add(value);
      }
      for (const variant of this.shopifyProduct.variants || []) {
        const value = String(variant.options?.[index] || '').trim();
        if (!value || seen.has(value)) continue;
        values.push({ value, swatchBackground: '', disabled: false });
        seen.add(value);
      }
      return values;
    }

    selectProductOption(index, value) {
      const input = this.pageOptionInputs(index).find((candidate) => String(candidate.value) === String(value));
      if (!input || input.disabled) return false;
      if (!input.checked) input.click();
      this.scheduleVariantSync();
      return true;
    }

    currentCustomizeUrl() {
      const sizeUrl = this.customizeUrlsBySize?.[this.currentSize()];
      return safeOrbeaUrl(sizeUrl)?.href || safeOrbeaUrl(this.dataset.customizeUrl)?.href || '';
    }

    currentProfile() {
      const variantId = this.variantIdFromPage();
      return this.stockProfiles.find((profile) => String(profile.variantId) === variantId
        || String(profile.variantId).endsWith(`/${variantId}`)) || null;
    }

    scheduleVariantSync() {
      this.syncTimers.forEach((timer) => window.clearTimeout(timer));
      this.syncTimers = [0, 100, 350, 900].map((delay) => window.setTimeout(() => this.syncVariant(), delay));
    }

    syncVariant(force = false) {
      const variant = this.currentVariant();
      if (!variant) return;
      const variantId = String(variant.id);
      if (!force && variantId === this.activeVariantId) {
        this.applyPageState();
        return;
      }
      const previousSize = this.activeSize;
      const hadSelection = Boolean(this.activeVariantId);
      this.restoreStockImage();
      this.restoreMainPrice();
      this.activeVariantId = variantId;
      this.dataset.selectedVariantId = variantId;
      this.activeSize = this.currentSize();
      const profile = this.currentProfile();
      if (!hadSelection || (profile && !this.componentChoiceDirty)) {
        this.initializeSelection(profile);
      } else {
        this.preserveSelection(previousSize);
      }
      this.captureInventorySource();
      if (!this.overlay?.hidden) this.render();
      this.applyPageState();
    }

    initializeSelection(profile) {
      const size = this.currentSize();
      this.selectedOptions = {};
      this.selectedMeasurements = {};
      this.selectedWheelColors = {};
      this.componentChoiceDirty = false;
      for (const group of this.config.groups || []) {
        const profileOptionId = profile?.components?.[group.id];
        const option = (group.options || []).find((candidate) => candidate.id === profileOptionId && candidate.available !== false)
          || (group.options || []).find((candidate) => candidate.id === group.defaultOptionId && candidate.available !== false)
          || (group.options || []).find((candidate) => candidate.available !== false);
        this.selectedOptions[group.id] = option?.id || '';
        for (const wheelOption of group.options || []) {
          const defaultColor = (wheelOption.wheelColors || []).find((color) => color.isDefault)
            || wheelOption.wheelColors?.[0];
          if (defaultColor) this.selectedWheelColors[`${group.id}:${wheelOption.id}`] = defaultColor.id;
        }
        const measurements = this.measurementsFor(group, option, size);
        const profileMeasurementCode = profile?.measurements?.[group.id];
        const measurement = measurements.find((candidate) => candidate.componentCode === profileMeasurementCode && candidate.available !== false)
          || measurements.find((candidate) => candidate.isDefault && candidate.available !== false)
          || measurements.find((candidate) => candidate.available !== false);
        if (measurement) this.selectedMeasurements[group.id] = measurement.id;
      }
    }

    preserveSelection(previousSize) {
      const size = this.currentSize();
      for (const group of this.config.groups || []) {
        const selected = (group.options || []).find((option) => option.id === this.selectedOptions[group.id]
          && option.available !== false);
        if (!selected) {
          const fallback = (group.options || []).find((option) => option.id === group.defaultOptionId && option.available !== false)
            || (group.options || []).find((option) => option.available !== false);
          this.selectedOptions[group.id] = fallback?.id || '';
        }

        for (const wheelOption of group.options || []) {
          const key = `${group.id}:${wheelOption.id}`;
          const selectedColorId = this.selectedWheelColors[key];
          const selectedColorAvailable = (wheelOption.wheelColors || []).some((color) => color.id === selectedColorId);
          if (selectedColorAvailable) continue;
          const defaultColor = this.defaultWheelColor(wheelOption);
          if (defaultColor) this.selectedWheelColors[key] = defaultColor.id;
        }

        if (!previousSize || previousSize === size) continue;
        const option = this.selectedOption(group);
        const measurements = this.measurementsFor(group, option, size);
        if (!measurements.length) continue;
        const previousMeasurement = this.measurementsFor(group, option, previousSize)
          .find((candidate) => candidate.id === this.selectedMeasurements[group.id]);
        const preserveMeasurement = !this.isCrankGroup(group) && !this.isCockpitGroup(group);
        const matchingMeasurement = preserveMeasurement && previousMeasurement
          ? measurements.find((candidate) => candidate.available !== false
            && (candidate.componentCode === previousMeasurement.componentCode || candidate.label === previousMeasurement.label))
          : null;
        const measurement = matchingMeasurement
          || measurements.find((candidate) => candidate.isDefault && candidate.available !== false)
          || measurements.find((candidate) => candidate.available !== false);
        if (measurement) this.selectedMeasurements[group.id] = measurement.id;
        else delete this.selectedMeasurements[group.id];
      }
    }

    selectedOption(group) {
      return (group.options || []).find((option) => option.id === this.selectedOptions[group.id])
        || (group.options || []).find((option) => option.id === group.defaultOptionId)
        || group.options?.[0]
        || null;
    }

    isCrankGroup(group) {
      return String(group?.name || '').toLowerCase() === 'crankset' || String(group?.id) === '32';
    }

    isCockpitGroup(group) {
      return ['handlebar', 'stem'].includes(String(group?.name || '').toLowerCase())
        || ['45', '48'].includes(String(group?.id));
    }

    isRotorGroup(group) {
      return String(group?.name || '').toLowerCase() === 'rotors' || String(group?.id) === '62';
    }

    isWheelGroup(group) {
      return ['wheels', 'wheelset'].includes(String(group?.name || '').toLowerCase()) || String(group?.id) === '85';
    }

    defaultWheelColor(option) {
      return (option?.wheelColors || []).find((color) => color.isDefault)
        || option?.wheelColors?.[0]
        || null;
    }

    selectedWheelColor(group, option) {
      const selectedId = this.selectedWheelColors[`${group?.id}:${option?.id}`];
      return (option?.wheelColors || []).find((color) => color.id === selectedId)
        || this.defaultWheelColor(option);
    }

    syncWheelColors(group, sourceOptionId, selectedColorId) {
      const sourceOption = (group?.options || []).find((option) => String(option.id) === String(sourceOptionId));
      const sourceColor = (sourceOption?.wheelColors || []).find((color) => String(color.id) === String(selectedColorId));
      if (!sourceColor) return;

      for (const option of group.options || []) {
        const colors = option.wheelColors || [];
        const matchingColor = colors.find((color) => String(color.id) === String(sourceColor.id))
          || colors.find((color) => sourceColor.sourceColorCode && color.sourceColorCode === sourceColor.sourceColorCode)
          || colors.find((color) => color.label === sourceColor.label);
        if (matchingColor) {
          this.selectedWheelColors[`${group.id}:${option.id}`] = matchingColor.id;
        } else if (colors.length === 1) {
          this.selectedWheelColors[`${group.id}:${option.id}`] = colors[0].id;
        }
      }
    }

    crankPowerMode(option) {
      return /powermeter|4iii/i.test(String(option?.name || '')) ? 'with' : 'without';
    }

    crankPowerLabel(option) {
      if (this.crankPowerMode(option) === 'without') return 'Zonder powermeter';
      const side = this.powerMeterMeasurementLabel(option);
      return ['Met powermeter', side].filter(Boolean).join(' ');
    }

    powerMeterMeasurementLabel(option) {
      if (this.crankPowerMode(option) === 'without') return '';
      const name = String(option?.name || '');
      const description = String(option?.description || '');
      if (/4iii/i.test(name)) return 'enkelzijdig';
      if (/left[- ]side|left crank arm|total power from the left leg/i.test(description)) return 'enkelzijdig';
      if (/r9200-p|campagnolo\s+(?:super\s+)?record|quarq|sram\s+red|sram\s+xx\s+sl|power\s*spider/i.test(name)) return 'dubbelzijdig';
      if (/power\s*spider|power meter in the chainring|power meter in the spider/i.test(description)) return 'dubbelzijdig';
      if (/dual[- ]sided|dual[- ]leg|each leg|left and right leg|independent power measurement for each leg/i.test(description)) {
        return 'dubbelzijdig';
      }
      return '';
    }

    crankModelName(option) {
      const name = String(option?.name || '').trim();
      const match = name.match(/^(.*?)\s+(?:\d{1,2}\s*[x×]\s*\d{1,2}|\d{1,2})T\b/i);
      const model = match?.[1]?.trim() || name;
      if (/4iii/i.test(name) && !/4iii/i.test(model)) return `${model} + 4iiii`;
      if (/quarq\s*d\s*zero/i.test(name) && !/quarq/i.test(model)) return `${model} + Quarq DZero`;
      return model;
    }

    crankChainring(option) {
      const name = String(option?.name || '');
      const double = name.match(/\b(\d{2})\s*[x×]\s*(\d{2})T?\b/i);
      if (double) return `${double[1]}x${double[2]}`;
      const single = name.match(/\b(\d{2})T\b/i);
      return single ? `${single[1]}T` : '';
    }

    crankPowerKey(option) {
      if (this.crankPowerMode(option) === 'without') return 'without';
      return ['with', this.powerMeterMeasurementLabel(option)].filter(Boolean).join('-');
    }

    crankOptionFor(group, powerKey, chainring) {
      return (group.options || []).find((option) => option.available !== false
        && this.crankPowerKey(option) === powerKey
        && this.crankChainring(option) === chainring) || null;
    }

    canUseCompactCrank(group) {
      const seen = new Set();
      for (const option of group.options || []) {
        if (option.available === false) continue;
        const chainring = this.crankChainring(option);
        const key = `${this.crankPowerKey(option)}:${chainring}`;
        if (!chainring || seen.has(key)) return false;
        seen.add(key);
      }
      return seen.size > 0;
    }

    setCrankSelection(group, powerKey, chainring) {
      const size = this.currentSize();
      const currentOption = this.selectedOption(group);
      const currentMeasurement = this.selectedMeasurement(group, currentOption, size);
      const nextOption = this.crankOptionFor(group, powerKey, chainring);
      if (!nextOption) return false;
      this.selectedOptions[group.id] = nextOption.id;
      delete this.selectedMeasurements[group.id];
      const nextMeasurements = this.measurementsFor(group, nextOption, size);
      const matchingMeasurement = nextMeasurements.find((measurement) => measurement.available !== false
        && measurement.label === currentMeasurement?.label);
      if (matchingMeasurement) this.selectedMeasurements[group.id] = matchingMeasurement.id;
      return true;
    }

    rotorSizes(option) {
      const match = String(option?.name || '').match(/(\d+)\s*mm\s*Front\s*\/\s*(\d+)\s*mm\s*Rear/i);
      return match ? { front: match[1], rear: match[2] } : { front: '', rear: '' };
    }

    rotorOptionFor(group, front, rear) {
      return (group.options || []).find((option) => {
        const sizes = this.rotorSizes(option);
        return option.available !== false && sizes.front === front && sizes.rear === rear;
      }) || null;
    }

    setRotorSelection(group, front, rear) {
      const option = this.rotorOptionFor(group, front, rear);
      if (!option) return false;
      this.selectedOptions[group.id] = option.id;
      return true;
    }

    measurementsFor(group, option, size) {
      const sourceSize = (this.config.sizes || []).find((entry) => String(entry.label) === String(size))?.code || size;
      const measurements = option?.measurementsBySize?.[sourceSize] || option?.measurementsBySize?.[size];
      return Array.isArray(measurements) ? measurements : [];
    }

    selectedMeasurement(group, option, size) {
      const measurements = this.measurementsFor(group, option, size);
      return measurements.find((measurement) => measurement.id === this.selectedMeasurements[group.id])
        || measurements.find((measurement) => measurement.isDefault && measurement.available !== false)
        || measurements.find((measurement) => measurement.available !== false)
        || null;
    }

    selectionMatchesProfile(profile) {
      if (!profile || String(profile.variantId) !== this.variantIdFromPage()) return false;
      const size = this.currentSize();
      if (profile.size && profile.size !== size) return false;
      for (const group of this.config.groups || []) {
        const option = this.selectedOption(group);
        if (!option || option.id !== profile.components?.[group.id]) return false;
        if (this.isWheelGroup(group)) {
          const selectedColor = this.selectedWheelColor(group, option);
          const defaultColor = this.defaultWheelColor(option);
          if (selectedColor?.id !== defaultColor?.id) return false;
        }
        const measurements = this.measurementsFor(group, option, size);
        if (measurements.length) {
          const measurement = this.selectedMeasurement(group, option, size);
          if (!measurement || measurement.componentCode !== profile.measurements?.[group.id]) return false;
        }
      }
      return true;
    }

    selectionIsDefault(size = this.currentSize()) {
      for (const group of this.config.groups || []) {
        const option = this.selectedOption(group);
        if (!option || option.id !== group.defaultOptionId) return false;
        if (this.isWheelGroup(group)) {
          const selectedColor = this.selectedWheelColor(group, option);
          const defaultColor = this.defaultWheelColor(option);
          if (selectedColor?.id !== defaultColor?.id) return false;
        }
        const measurements = this.measurementsFor(group, option, size);
        if (measurements.length) {
          const selected = this.selectedMeasurement(group, option, size);
          const defaultMeasurement = measurements.find((measurement) => measurement.isDefault && measurement.available !== false)
            || measurements.find((measurement) => measurement.available !== false);
          if (selected?.id !== defaultMeasurement?.id) return false;
        }
      }
      return true;
    }

    markConfigurationEdited() {
      this.componentChoiceDirty = !this.selectionMatchesProfile(this.currentProfile()) && !this.selectionIsDefault();
    }

    isMyOVariant() {
      return /myo/i.test(this.currentColor()) || /CM$/i.test(String(this.currentVariant()?.sku || ''));
    }

    selectionFingerprint() {
      const payload = this.selectionPayload();
      return JSON.stringify({
        size: payload.size,
        color: payload.color,
        items: payload.items.map((item) => [item.groupId, item.componentCode, item.measurementCode, item.wheelColorCode]),
      });
    }

    currentMyoDesign() {
      const variantId = this.variantIdFromPage();
      const design = this.myoDesignsByVariant.get(variantId) || null;
      if (!design || design.fingerprint === this.selectionFingerprint()) return design;
      this.myoDesignsByVariant.delete(variantId);
      return null;
    }

    setMyoStatus(message, state = '') {
      if (!this.myoStatus) return;
      this.myoStatus.textContent = message;
      if (state) this.myoStatus.dataset.state = state;
      else delete this.myoStatus.dataset.state;
    }

    attachMyoDesign() {
      const design = parseOrbeaCustomization(this.myoInput?.value, this.currentCustomizeUrl());
      if (!design) {
        this.setMyoStatus('Vul een geldige Orbea-code van 10 tekens of een Orbea-deellink in.', 'error');
        this.myoInput?.setAttribute('aria-invalid', 'true');
        this.myoInput?.focus();
        return;
      }
      design.fingerprint = this.selectionFingerprint();
      this.myoDesignsByVariant.set(this.variantIdFromPage(), design);
      this.myoInput?.removeAttribute('aria-invalid');
      this.applyPageState();
    }

    updateMyoLinkPanel(profileMatch) {
      if (!this.myoLinkPanel) return;
      const visible = this.isMyOVariant() && !profileMatch;
      this.myoLinkPanel.hidden = !visible;
      if (!visible) return;

      const design = this.currentMyoDesign();
      const fallbackUrl = this.currentCustomizeUrl();
      if (this.myoOpenLink) {
        this.myoOpenLink.hidden = !fallbackUrl && !design?.url;
        this.myoOpenLink.href = design?.url || fallbackUrl || '#';
        this.myoOpenLink.textContent = design ? 'Ontwerp bij Orbea bekijken' : 'Kleur ontwerpen bij Orbea';
      }
      if (this.myoInput && (design || document.activeElement !== this.myoInput)) {
        this.myoInput.value = design?.code || '';
      }
      if (design) {
        this.myoInput?.removeAttribute('aria-invalid');
        this.setMyoStatus(`Orbea-ontwerp gekoppeld · code ${design.code}`, 'success');
        if (this.myoAttachButton) this.myoAttachButton.textContent = 'Code wijzigen';
      } else {
        this.setMyoStatus('Na het koppelen van de code kun je de fiets aan de winkelwagen toevoegen.');
        if (this.myoAttachButton) this.myoAttachButton.textContent = 'Ontwerp koppelen';
      }
    }

    selectedDelta() {
      return (this.config.groups || []).reduce((total, group) => total + Number(this.selectedOption(group)?.deltaCents || 0), 0);
    }

    effectivePrice(profileMatch) {
      if (profileMatch?.priceCents) return Number(profileMatch.priceCents);
      return Number(this.currentVariant()?.priceCents || this.config.product?.basePriceCents || 0) + this.selectedDelta();
    }

    carrierVariantId(profileMatch) {
      if (profileMatch) return '';
      return String(this.priceMap?.prices?.[String(this.effectivePrice(profileMatch))] || '');
    }

    formatMoney(cents, signed = false) {
      const amount = Number(cents || 0) / 100;
      const formatted = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(Math.abs(amount));
      if (!signed || amount === 0) return formatted;
      return `${amount > 0 ? '+' : '−'}${formatted}`;
    }

    todayAtNoon() {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      return date;
    }

    parseIsoDate(value) {
      const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!match) return null;
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    addDays(date, days) {
      const next = new Date(date);
      next.setDate(next.getDate() + Number(days || 0));
      return next;
    }

    formatDate(date) {
      return new Intl.DateTimeFormat('nl-NL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    }

    formatIsoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    productionDate(variant = this.currentVariant()) {
      const today = this.todayAtNoon();
      const futureDates = [variant?.availabilityDate, variant?.specializedExpectedDate]
        .map((value) => this.parseIsoDate(value))
        .filter((date) => date && date > today);
      const backorderDate = this.parseIsoDate(variant?.backorderDate);
      if (backorderDate) futureDates.push(backorderDate > today ? backorderDate : today);
      return futureDates.sort((left, right) => left - right)[0] || null;
    }

    pickupDateForStandard(productionDate) {
      let pickupDate = this.addDays(productionDate, 15);
      const weekday = pickupDate.getDay();
      if (weekday === 0) pickupDate = this.addDays(pickupDate, 3);
      else if (weekday === 1) pickupDate = this.addDays(pickupDate, 2);
      else if (weekday === 2) pickupDate = this.addDays(pickupDate, 1);
      return pickupDate;
    }

    timelineIconMarkup() {
      return `<svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16" fill="none">
        <circle cx="10" cy="10" r="8.25" stroke="currentColor" stroke-width="1.5"></circle>
        <path d="M10 8.4v5M10 5.8v.1" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"></path>
      </svg>`;
    }

    timelineMarkup(productionDate, pickupDate, disclaimer = '') {
      const panelId = `OrbeaDelivery-${String(this.id || 'configurator').replace(/[^a-z0-9_-]/gi, '')}-${this.variantIdFromPage()}`;
      return `<details class="inventory-timeline">
        <summary class="inventory-timeline__summary" aria-label="Bekijk leverplanning" aria-controls="${escapeHtml(panelId)}">
          <span class="inventory-timeline__summary-icon">${this.timelineIconMarkup()}</span>
        </summary>
        <div class="inventory-timeline__panel" id="${escapeHtml(panelId)}">
          <span class="inventory-timeline__heading">Leverplanning</span>
          <ol class="inventory-timeline__list">
            <li class="inventory-timeline__item">
              <span class="inventory-timeline__dot" aria-hidden="true"></span>
              <span><strong>${escapeHtml(this.formatDate(this.todayAtNoon()))}:</strong> De fiets wordt voor je besteld en gereserveerd bij Orbea.</span>
            </li>
            <li class="inventory-timeline__item">
              <span class="inventory-timeline__dot" aria-hidden="true"></span>
              <span><strong>Rond ${escapeHtml(this.formatDate(productionDate))}:</strong> De fiets gaat in productie bij Orbea (lakken, opbouwen en afmonteren).</span>
            </li>
            <li class="inventory-timeline__item inventory-timeline__item--final">
              <span class="inventory-timeline__dot" aria-hidden="true"></span>
              <span><strong>Rond ${escapeHtml(this.formatDate(pickupDate))}:</strong> De fiets staat klaar om bij ons op te halen.</span>
            </li>
          </ol>
          ${disclaimer ? `<p class="orbea-configurator__timeline-disclaimer">${escapeHtml(disclaimer)}</p>` : ''}
        </div>
      </details>`;
    }

    myODeliveryMarkup(configurationChanged) {
      const today = this.todayAtNoon();
      const sourceDate = this.productionDate();
      const sourceWeeks = sourceDate ? Math.max(0, Math.ceil((sourceDate - today) / 604800000)) : 0;
      const startWeeks = Math.max(6, sourceWeeks);
      const endWeeks = startWeeks + 4;
      const productionDate = sourceDate && sourceWeeks >= 6 ? sourceDate : this.addDays(today, 42);
      const pickupDate = this.addDays(productionDate, 28);
      const dot = endWeeks > 10
        ? { outer: 'rgb(255,165,0,0.3)', inner: 'rgb(255,165,0)' }
        : { outer: 'rgb(196,222,15,0.3)', inner: 'rgb(255,255,0)' };
      const disclaimer = configurationChanged
        ? 'Door wijzigingen in de componentkeuze is deze planning indicatief. Productie- en leverdata kunnen wijzigen. Hieraan kunnen geen rechten worden ontleend.'
        : 'Deze planning is indicatief. Productie- en leverdata kunnen wijzigen. Hieraan kunnen geen rechten worden ontleend.';
      return `<svg width="15" height="15" aria-hidden="true">
        <circle cx="7.5" cy="7.5" r="7.5" fill="${dot.outer}"></circle>
        <circle cx="7.5" cy="7.5" r="5" stroke="rgb(0,0,0)" stroke-width="1" fill="${dot.inner}"></circle>
      </svg>
      <span class="product__inventory-text">Verwachte levertijd MyO: ${startWeeks}–${endWeeks} weken.</span>
      ${this.timelineMarkup(productionDate, pickupDate, disclaimer)}`;
    }

    restoreInventory(inventory) {
      const original = this.inventoryRestoreByVariant.get(this.variantIdFromPage());
      if (inventory && original != null) inventory.innerHTML = original;
      if (inventory) delete inventory.dataset.orbeaConfiguration;
      return original;
    }

    enhanceStandardTimeline(inventory, configurationChanged) {
      const variant = this.currentVariant();
      const productionDate = this.productionDate(variant);
      if (!inventory
        || Number(variant?.inventoryQuantity || 0) > 0
        || String(variant?.inventoryPolicy || '').toLowerCase() !== 'continue'
        || variant?.directAvailable
        || !productionDate) return;

      const pickupDate = this.pickupDateForStandard(productionDate);
      const disclaimer = configurationChanged
        ? 'Door wijzigingen in de componentkeuze is deze planning indicatief. Productie- en leverdata kunnen wijzigen. Hieraan kunnen geen rechten worden ontleend.'
        : '';
      const currentTimeline = inventory.querySelector('.inventory-timeline');
      if (currentTimeline) currentTimeline.outerHTML = this.timelineMarkup(productionDate, pickupDate, disclaimer);
      else inventory.insertAdjacentHTML('beforeend', this.timelineMarkup(productionDate, pickupDate, disclaimer));
      inventory.dataset.orbeaConfiguration = configurationChanged ? 'standard-customized' : 'standard-production';
    }

    selectionPayload() {
      const size = this.currentSize();
      const items = (this.config.groups || []).map((group) => {
        const option = this.selectedOption(group);
        const measurement = this.selectedMeasurement(group, option, size);
        const wheelColor = this.isWheelGroup(group) ? this.selectedWheelColor(group, option) : null;
        return {
          groupId: group.id,
          group: groupLabel(group),
          componentCode: option?.componentCode || option?.id || '',
          component: String(group.name || '').toLowerCase() === 'tools'
            ? toolsPresentation(option).title + (toolsPresentation(option).details.length ? ` · ${option.name}` : '')
            : option?.name || '',
          deltaCents: Number(option?.deltaCents || 0),
          measurementCode: measurement?.componentCode || '',
          measurement: measurement?.label || '',
          wheelColorCode: wheelColor?.sourceColorCode || '',
          wheelColor: wheelColor?.label || '',
        };
      });
      return { size, color: this.currentColor(), deltaCents: this.selectedDelta(), items };
    }

    summaryValue(item) {
      const group = (this.config.groups || []).find((candidate) => String(candidate.id) === String(item.groupId));
      const option = group ? this.selectedOption(group) : null;
      if (group && this.isCrankGroup(group)) {
        const model = this.crankModelName(option);
        const chainring = this.crankChainring(option).replace('x', '×');
        return [model, chainring, item.measurement ? `${item.measurement} mm` : ''].filter(Boolean).join(' · ');
      }
      if (group && this.isRotorGroup(group)) {
        const sizes = this.rotorSizes(option);
        return `${sizes.front} mm voor · ${sizes.rear} mm achter`;
      }
      if (String(group?.name || '').toLowerCase() === 'cassette') {
        const cassette = cassettePresentation(option);
        if (cassette) {
          const type = cassette.details.find((detail) => !/-speed$/i.test(detail)) || '';
          return [type, cassette.title.replace(/T$/i, '')].filter(Boolean).join(' · ');
        }
      }
      if (String(group?.name || '').toLowerCase() === 'saddle') {
        const saddle = saddlePresentation(option);
        const width = saddle.details.find((detail) => /^Breedte:/i.test(detail))?.replace(/^Breedte:\s*/i, '') || '';
        return [option?.name, width].filter(Boolean).join(' · ');
      }
      if (String(group?.name || '').toLowerCase() === 'mudguard') {
        return mudguardPresentation(option).title;
      }
      if (group && this.isWheelGroup(group)) {
        return [item.component, item.wheelColor].filter(Boolean).join('\n');
      }
      const measurement = item.measurement ? `${item.measurement} mm` : '';
      return [item.component, item.wheelColor, measurement].filter(Boolean).join(' · ');
    }

    renderSavedSummary(profileMatch) {
      if (!this.savedSummary) return;
      const payload = this.selectionPayload();
      const metadata = [
        `<span><strong>Maat</strong> ${escapeHtml(payload.size || 'nog niet gekozen')}</span>`,
      ];
      const selectedColor = profileMatch?.color || payload.color;
      if (selectedColor) {
        metadata.push(`<span><strong>Kleur</strong> ${escapeHtml(selectedColor)}</span>`);
      }
      const items = payload.items.map((item) => `
        <div class="orbea-configurator__saved-item">
          <dt>${escapeHtml(item.group)}</dt>
          <dd>${escapeHtml(this.summaryValue(item))}</dd>
        </div>`).join('');
      this.savedSummary.innerHTML = `
        <div class="orbea-configurator__saved-header">
          <strong>Geselecteerde configuratie</strong>
          ${profileMatch ? '<span class="orbea-configurator__saved-badge">Winkelvoorraad</span>' : ''}
        </div>
        <div class="orbea-configurator__saved-meta">${metadata.join('')}</div>
        <dl class="orbea-configurator__saved-list">${items}</dl>`;
      this.savedSummary.hidden = false;
    }

    lineItemPromiseProperties(profileMatch) {
      if (profileMatch) return {};

      const today = this.todayAtNoon();
      const variant = this.currentVariant();
      const productionDate = this.productionDate(variant);
      let pickupDate;
      let route;
      let prefix;
      let dot;
      let hold;
      let message;

      if (this.isMyOVariant()) {
        const sourceWeeks = productionDate ? Math.max(0, Math.ceil((productionDate - today) / 604800000)) : 0;
        const startWeeks = Math.max(6, sourceWeeks);
        const endWeeks = startWeeks + 4;
        const effectiveProductionDate = productionDate && sourceWeeks >= 6
          ? productionDate
          : this.addDays(today, 42);
        pickupDate = this.addDays(effectiveProductionDate, 28);
        route = 'supplier_future';
        prefix = 'rond';
        dot = endWeeks > 10 ? 'orange' : 'yellow';
        hold = 'true';
        message = `Verwachte levertijd MyO: ${startWeeks}–${endWeeks} weken.`;
      } else if (productionDate) {
        pickupDate = this.pickupDateForStandard(productionDate);
        route = 'supplier_future';
        prefix = 'rond';
        dot = pickupDate > this.addDays(today, 60) ? 'orange' : 'yellow';
        hold = 'true';
        message = `Bestel nu: rond ${this.formatDate(pickupDate)} af te halen.`;
      } else {
        pickupDate = this.addDays(today, 20);
        const weekday = pickupDate.getDay();
        if (weekday === 0) pickupDate = this.addDays(pickupDate, 3);
        else if (weekday === 1) pickupDate = this.addDays(pickupDate, 2);
        else if (weekday === 2) pickupDate = this.addDays(pickupDate, 1);
        route = 'supplier_stock';
        prefix = 'uiterlijk';
        dot = 'lime';
        hold = 'false';
        message = `Bestel nu: uiterlijk ${this.formatDate(pickupDate)} af te halen.`;
      }

      return {
        '_2ws_promise_version': '1',
        '_2ws_promise_profile': 'bike',
        '_2ws_promise_route': route,
        '_2ws_promise_prefix': prefix,
        '_2ws_promise_date': this.formatIsoDate(pickupDate),
        '_2ws_inventory_message': message,
        '_2ws_promise_dot': dot,
        '_2ws_backorder_hold': hold,
        '_2ws_supplier_date': productionDate ? this.formatIsoDate(productionDate) : '',
        '_2ws_supplier_source': productionDate ? 'orbea_configurator' : '',
        '_2ws_own_offset_days': '12',
        '_2ws_supplier_offset_days': '20',
        '_2ws_future_offset_days': '15',
        '_2ws_own_stock_at_add': '0',
        '_2ws_shipping_group': 'ship_complete',
      };
    }

    writeLineItemProperties(profileMatch, configurationChanged, myoDesign) {
      const form = this.productForm()?.querySelector('form');
      const payload = this.selectionPayload();
      const selectedColor = profileMatch?.color || payload.color;
      const execution = profileMatch
        ? 'Winkelvoorraad'
        : this.isMyOVariant() ? 'MyO-configuratie' : 'Standaardkleur met componentconfiguratie';
      const properties = {
        '_Orbea bronproduct': this.shopifyProduct.productId || '',
        '_Orbea bronproduct-handle': this.shopifyProduct.handle || '',
        '_Orbea bronvariant': this.variantIdFromPage(),
        '_Orbea configuratiecodes': JSON.stringify(payload.items.map((item) => [item.groupId, item.componentCode, item.measurementCode, item.wheelColorCode])),
        '_Orbea uitvoering': execution,
        '_Orbea kleur': selectedColor,
        '_Orbea fietsmaat': payload.size,
        'Maat': payload.size,
        'Kleur': selectedColor,
      };
      Object.assign(properties, this.lineItemPromiseProperties(profileMatch));
      if (profileMatch) properties['_Orbea voorraadprofiel'] = profileMatch.sku || '';
      if (myoDesign) {
        properties['MyO-code'] = myoDesign.code;
        properties['_Orbea configuratiecode'] = myoDesign.code;
        properties['_Orbea configuratielink'] = myoDesign.url;
      }
      if (configurationChanged) {
        properties['_Orbea leveringsdisclaimer'] = 'Door wijzigingen in de componentkeuze is de leverplanning indicatief. Productie- en leverdata kunnen wijzigen. Hieraan kunnen geen rechten worden ontleend.';
      }
      for (const item of payload.items) {
        properties[item.group] = this.summaryValue(item).replace(/\n+/g, ' · ');
      }
      if (!form) return properties;
      form.querySelectorAll('[data-orbea-property]').forEach((input) => input.remove());
      for (const [key, value] of Object.entries(properties)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = String(value);
        input.setAttribute('value', String(value));
        input.dataset.orbeaProperty = 'true';
        form.appendChild(input);
      }
      return properties;
    }

    dispatchFormState(profileMatch, requiresCustomize, myoDesign, lineItemProperties) {
      const form = this.productForm();
      if (!form) return;
      form.dispatchEvent(new CustomEvent('orbea:configuration-state', {
        bubbles: true,
        detail: {
          variantId: this.variantIdFromPage(),
          stockMatch: Boolean(profileMatch),
          requiresCustomize,
          myoDesignLinked: Boolean(myoDesign),
          lineItemProperties,
          customizeUrl: this.currentCustomizeUrl(),
          testMode: this.testMode,
          priceCents: this.effectivePrice(profileMatch),
          carrierVariantId: this.carrierVariantId(profileMatch),
        },
      }));
    }

    captureInventorySource() {
      const inventory = this.productInfo.querySelector?.('[id^="Inventory-"]');
      if (!inventory || inventory.dataset.orbeaConfiguration) return;
      this.inventoryRestoreByVariant.set(this.variantIdFromPage(), inventory.innerHTML);
    }

    applyFulfilment(profileMatch, requiresCustomize) {
      const inventory = this.productInfo.querySelector?.('[id^="Inventory-"]');
      if (this.fulfilmentNode) this.fulfilmentNode.hidden = true;
      if (profileMatch) {
        this.restoreInventory(inventory);
        return;
      }

      this.restoreInventory(inventory);
      if (this.isMyOVariant()) {
        if (inventory) {
          inventory.dataset.orbeaConfiguration = 'myo';
          inventory.innerHTML = this.myODeliveryMarkup(requiresCustomize && !this.selectionIsDefault());
        }
        return;
      }

      this.enhanceStandardTimeline(inventory, requiresCustomize);
    }

    applyStockImage(profileMatch) {
      this.restoreStockImage();
      if (!profileMatch?.stockImageUrl || !profileMatch.fallbackMediaId) return;
      const url = safeImageUrl(profileMatch.stockImageUrl);
      if (!url) return;
      const mediaId = String(profileMatch.fallbackMediaId).replace(/[^0-9]/g, '');
      if (!mediaId) return;
      const targets = document.querySelectorAll(`[data-media-id$="-${mediaId}"], [data-target$="-${mediaId}"]`);
      const elements = new Set();
      targets.forEach((target) => target.querySelectorAll('img, source').forEach((element) => elements.add(element)));
      elements.forEach((element) => {
        const attributes = {};
        for (const name of ['src', 'srcset', 'sizes']) attributes[name] = element.getAttribute(name);
        this.stockImageRestore.push({ element, attributes });
        if (element.tagName === 'SOURCE') {
          element.setAttribute('srcset', url);
        } else {
          element.setAttribute('src', url);
          element.setAttribute('srcset', url);
          element.removeAttribute('sizes');
          const productName = String(this.config.product?.name || 'fiets');
          element.alt = `${/^orbea\b/i.test(productName) ? productName : `Orbea ${productName}`} in ${profileMatch.color} uit winkelvoorraad`;
        }
      });
      const sectionId = this.productInfo.dataset?.section;
      this.productInfo.querySelector?.('media-gallery')?.setActiveMedia?.(`${sectionId}-${mediaId}`, false);
    }

    restoreStockImage() {
      for (const { element, attributes } of this.stockImageRestore || []) {
        if (!element?.isConnected) continue;
        for (const [name, value] of Object.entries(attributes)) {
          if (value == null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }
      }
      this.stockImageRestore = [];
    }

    applyMainPrice(profileMatch, requiresCustomize) {
      this.restoreMainPrice();
      if (!profileMatch && !requiresCustomize) return;
      const priceRoot = this.productInfo.querySelector?.('[id^="price-"]');
      if (!priceRoot) return;
      this.priceRestore = { element: priceRoot, html: priceRoot.innerHTML };
      const label = profileMatch ? 'Prijs winkelconfiguratie' : 'Configuratieprijs';
      priceRoot.innerHTML = `<div class="orbea-configurator__main-price"><span>${escapeHtml(label)}</span><strong>${escapeHtml(this.formatMoney(this.effectivePrice(profileMatch)))}</strong></div>`;
    }

    restoreMainPrice() {
      if (this.priceRestore?.element?.isConnected) this.priceRestore.element.innerHTML = this.priceRestore.html;
      this.priceRestore = null;
    }

    applyPageState() {
      const profile = this.currentProfile();
      const profileMatch = this.selectionMatchesProfile(profile) ? profile : null;
      const configurationChanged = !profileMatch && !this.selectionIsDefault();
      const myoDesign = profileMatch ? null : this.currentMyoDesign();
      const requiresCustomize = !profileMatch && this.isMyOVariant() && !myoDesign;
      const lineItemProperties = this.writeLineItemProperties(profileMatch, configurationChanged, myoDesign);
      this.dispatchFormState(profileMatch, requiresCustomize, myoDesign, lineItemProperties);
      this.applyFulfilment(profileMatch, configurationChanged);
      this.applyStockImage(profileMatch);
      this.applyMainPrice(profileMatch, configurationChanged || requiresCustomize);
      this.updateTotals();
      this.renderSavedSummary(profileMatch);
      this.updateMyoLinkPanel(profileMatch);
      return { profileMatch, requiresCustomize, myoDesign };
    }

    wheelColorMarkup(group, option) {
      if (!this.isWheelGroup(group) || !(option.wheelColors || []).length) return '';
      const selected = this.selectedWheelColor(group, option);
      const swatches = option.wheelColors.map((color, index) => {
        const inputId = `${this.id}-${group.id}-${option.id}-wheel-color-${index}`;
        const colors = (color.swatchColors || [])
          .filter((value) => /^#[0-9a-f]{3,8}$/i.test(String(value)))
          .map((value) => `<span style="--orbea-wheel-swatch-color:${escapeHtml(value)}"></span>`)
          .join('');
        return `<span class="orbea-configurator__wheel-color-choice">
          <input class="orbea-configurator__wheel-color-input" type="radio" id="${escapeHtml(inputId)}"
            name="${escapeHtml(`${this.id}-${group.id}-${option.id}-wheel-color`)}" value="${escapeHtml(color.id)}"
            data-orbea-wheel-color="${escapeHtml(group.id)}" data-orbea-wheel-option="${escapeHtml(option.id)}"
            ${selected?.id === color.id ? 'checked' : ''}>
          <label class="orbea-configurator__wheel-color-swatch" for="${escapeHtml(inputId)}" title="${escapeHtml(color.label)}">
            <span class="orbea-configurator__wheel-color-paint" aria-hidden="true">${colors}</span>
            <span class="visually-hidden">${escapeHtml(color.label)}</span>
          </label>
        </span>`;
      }).join('');
      return `<div class="orbea-configurator__wheel-colors" role="radiogroup" aria-label="Kleur wielset">
        <span class="orbea-configurator__wheel-color-swatches">${swatches}</span>
        <span class="orbea-configurator__wheel-color-selected" aria-live="polite">${escapeHtml(selected?.label || '')}</span>
      </div>`;
    }

    optionMarkup(group, option, index) {
      const inputId = `${this.id}-${group.id}-${index}`;
      const omitImage = String(group.name || '').toLowerCase() === 'rotors' || String(group.id) === '62';
      const selectedWheelColor = this.isWheelGroup(group) ? this.selectedWheelColor(group, option) : null;
      const image = safeImageUrl(selectedWheelColor?.cachedImageUrl || selectedWheelColor?.imageUrl || option.cachedImageUrl || option.imageUrl);
      const crop = safeImageCrop(selectedWheelColor?.imageCrop || option.imageCrop);
      const description = optionDescription(group, option);
      const cassette = String(group.name || '').toLowerCase() === 'cassette' ? cassettePresentation(option) : null;
      const wheel = ['wheels', 'wheelset'].includes(String(group.name || '').toLowerCase()) ? wheelPresentation(option) : null;
      const saddle = String(group.name || '').toLowerCase() === 'saddle' ? saddlePresentation(option) : null;
      const mudguard = String(group.name || '').toLowerCase() === 'mudguard' ? mudguardPresentation(option) : null;
      const tools = String(group.name || '').toLowerCase() === 'tools' ? toolsPresentation(option) : null;
      const axle = /^(?:front|rear) wheel axle$/i.test(String(group.name || '')) ? axlePresentation(option) : null;
      const isHandlebar = String(group.name || '').toLowerCase() === 'handlebar';
      const isStem = String(group.name || '').toLowerCase() === 'stem';
      const isCrank = this.isCrankGroup(group);
      const hasSeparateStem = (this.config.groups || []).some((candidate) => String(candidate.name || '').toLowerCase() === 'stem');
      const handlebarTitle = /cockpit|integrated|geïntegreerd/i.test(String(option.name || ''))
        ? 'Geïntegreerde cockpit'
        : hasSeparateStem ? 'Stuur' : 'Stuur + stuurpen';
      const title = cassette?.title || wheel?.title || saddle?.title || mudguard?.title || tools?.title || axle?.title
        || (isHandlebar ? handlebarTitle : isStem ? 'Stuurpen' : option.name);
      const powerLabel = isCrank && this.crankPowerMode(option) === 'with' ? this.crankPowerLabel(option) : '';
      const details = cassette?.details || wheel?.details || saddle?.details || mudguard?.details || tools?.details || axle?.details
        || (isHandlebar || isStem ? [option.name] : powerLabel ? [powerLabel] : description ? [description] : []);
      const imageMarkup = !omitImage && image
        ? `<img class="orbea-configurator__image" src="${escapeHtml(image)}" alt="${escapeHtml(option.name)}" width="300" height="300" loading="${crop ? 'eager' : 'lazy'}" data-orbea-component-image${crop ? ` data-orbea-image-crop="${crop.join(',')}"` : ''}>`
        : '';
      const mediaMarkup = imageMarkup ? `<span class="orbea-configurator__image-wrap">${imageMarkup}</span>` : '';
      const textOnly = omitImage || !imageMarkup;
      const card = `
        <input class="orbea-configurator__option-input" type="radio" id="${escapeHtml(inputId)}"
          name="${escapeHtml(`${this.id}-${group.id}`)}" value="${escapeHtml(option.id)}"
          data-orbea-option="${escapeHtml(group.id)}" ${this.selectedOptions[group.id] === option.id ? 'checked' : ''}
          ${option.available === false ? 'disabled' : ''}>
        <label class="orbea-configurator__option${textOnly ? ' orbea-configurator__option--text-only' : ''}" for="${escapeHtml(inputId)}">
          ${mediaMarkup}
          <span class="orbea-configurator__option-copy">
            <span class="orbea-configurator__option-name">${escapeHtml(title)}</span>
            ${details.length ? `<span class="orbea-configurator__option-description">${details.map((line) => escapeHtml(line)).join('<br>')}</span>` : ''}
            <span class="orbea-configurator__price">${escapeHtml(this.formatMoney(option.deltaCents, true))}</span>
          </span>
        </label>`;
      const wheelColors = this.wheelColorMarkup(group, option);
      return wheelColors ? `<div class="orbea-configurator__option-wrap">${card}${wheelColors}</div>` : card;
    }

    crankPowerMarkup(group) {
      const selected = this.selectedOption(group);
      const selectedPowerKey = this.crankPowerKey(selected);
      const selectedChainring = this.crankChainring(selected);
      const powerKeys = [...new Set((group.options || [])
        .filter((option) => option.available !== false)
        .map((option) => this.crankPowerKey(option)))];
      return powerKeys.map((powerKey, index) => {
        const option = this.crankOptionFor(group, powerKey, selectedChainring)
          || (group.options || []).find((candidate) => candidate.available !== false && this.crankPowerKey(candidate) === powerKey);
        if (!option) return '';
        const inputId = `${this.id}-${group.id}-power-${index}`;
        const image = safeImageUrl(option.cachedImageUrl || option.imageUrl);
        const crop = safeImageCrop(option.imageCrop);
        const imageMarkup = image
          ? `<img class="orbea-configurator__image" src="${escapeHtml(image)}" alt="${escapeHtml(option.name)}" width="300" height="300" loading="${crop ? 'eager' : 'lazy'}" data-orbea-component-image${crop ? ` data-orbea-image-crop="${crop.join(',')}"` : ''}>`
          : '';
        const name = this.crankPowerLabel(option);
        const details = [this.crankModelName(option)];
        return `
          <input class="orbea-configurator__option-input" type="radio" id="${escapeHtml(inputId)}"
            name="${escapeHtml(`${this.id}-${group.id}-power`)}" value="${escapeHtml(powerKey)}"
            data-orbea-crank-power="${escapeHtml(group.id)}" ${selectedPowerKey === powerKey ? 'checked' : ''}>
          <label class="orbea-configurator__option${imageMarkup ? '' : ' orbea-configurator__option--text-only'}" for="${escapeHtml(inputId)}">
            ${imageMarkup ? `<span class="orbea-configurator__image-wrap">${imageMarkup}</span>` : ''}
            <span class="orbea-configurator__option-copy">
              <span class="orbea-configurator__option-name">${name}</span>
              <span class="orbea-configurator__option-description">${details.map((line) => escapeHtml(line)).join('<br>')}</span>
              <span class="orbea-configurator__price">${escapeHtml(this.formatMoney(option.deltaCents, true))}</span>
            </span>
          </label>`;
      }).join('');
    }

    crankChainringMarkup(group) {
      const selected = this.selectedOption(group);
      const selectedPowerKey = this.crankPowerKey(selected);
      const selectedChainring = this.crankChainring(selected);
      const chainrings = [...new Set((group.options || []).map((option) => this.crankChainring(option)).filter(Boolean))]
        .sort((left, right) => Number.parseInt(right, 10) - Number.parseInt(left, 10));
      const items = chainrings.map((chainring, index) => {
        const inputId = `${this.id}-${group.id}-chainring-${index}`;
        const available = Boolean(this.crankOptionFor(group, selectedPowerKey, chainring));
        return `
          <input class="orbea-configurator__measurement-input" type="radio" id="${escapeHtml(inputId)}"
            name="${escapeHtml(`${this.id}-${group.id}-chainring`)}" value="${escapeHtml(chainring)}"
            data-orbea-crank-chainring="${escapeHtml(group.id)}" ${selectedChainring === chainring ? 'checked' : ''}
            ${available ? '' : 'disabled'}>
          <label class="orbea-configurator__measurement-label" for="${escapeHtml(inputId)}">${escapeHtml(chainring.replace('x', '×'))}</label>`;
      }).join('');
      return `<div class="orbea-configurator__measurements"><p class="orbea-configurator__measurement-title">Kettingbladen</p><div class="orbea-configurator__measurement-list">${items}</div></div>`;
    }

    rotorMarkup(group) {
      const selected = this.rotorSizes(this.selectedOption(group));
      const values = ['140', '160'];
      const selector = (position, title) => {
        const items = values.map((value, index) => {
          const front = position === 'front' ? value : selected.front;
          const rear = position === 'rear' ? value : selected.rear;
          const available = Boolean(this.rotorOptionFor(group, front, rear));
          const inputId = `${this.id}-${group.id}-${position}-${index}`;
          return `
            <input class="orbea-configurator__measurement-input" type="radio" id="${escapeHtml(inputId)}"
              name="${escapeHtml(`${this.id}-${group.id}-${position}`)}" value="${value}"
              data-orbea-rotor-${position}="${escapeHtml(group.id)}" ${selected[position] === value ? 'checked' : ''}
              ${available ? '' : 'disabled'}>
            <label class="orbea-configurator__measurement-label" for="${escapeHtml(inputId)}">${value} mm</label>`;
        }).join('');
        return `<div class="orbea-configurator__measurements"><p class="orbea-configurator__measurement-title">${title}</p><div class="orbea-configurator__measurement-list">${items}</div></div>`;
      };
      return `<div class="orbea-configurator__suboptions">${selector('front', 'Voor')} ${selector('rear', 'Achter')}</div>`;
    }

    orderedOptions(group) {
      const options = [...(group.options || [])];
      const isWheelGroup = String(group.name || '').toLowerCase() === 'wheels' || String(group.id) === '85';
      if (!isWheelGroup) return options;
      const rimHeight = (option) => {
        const match = String(option?.name || '').match(/\b(?:RA|RP)\s*(\d{2})/i);
        return match ? Number(match[1]) : 0;
      };
      return options.sort((left, right) => {
        const leftTeam = /TEAM\b/i.test(String(left?.name || ''));
        const rightTeam = /TEAM\b/i.test(String(right?.name || ''));
        if (leftTeam !== rightTeam) return leftTeam ? 1 : -1;
        const heightDifference = rimHeight(right) - rimHeight(left);
        if (heightDifference) return heightDifference;
        return Number(right.deltaCents || 0) - Number(left.deltaCents || 0);
      });
    }

    measurementMarkup(group, option, size) {
      const measurements = this.measurementsFor(group, option, size);
      if (!measurements.length) return '';
      const selected = this.selectedMeasurement(group, option, size);
      const title = MEASUREMENT_LABELS[String(measurements[0]?.concept || '').toLowerCase()] || 'Maatvoering';
      const items = measurements.map((measurement, index) => {
        const inputId = `${this.id}-${group.id}-measurement-${index}`;
        return `
          <input class="orbea-configurator__measurement-input" type="radio" id="${escapeHtml(inputId)}"
            name="${escapeHtml(`${this.id}-${group.id}-measurement`)}" value="${escapeHtml(measurement.id)}"
            data-orbea-measurement="${escapeHtml(group.id)}" ${selected?.id === measurement.id ? 'checked' : ''}
            ${measurement.available === false ? 'disabled' : ''}>
          <label class="orbea-configurator__measurement-label" for="${escapeHtml(inputId)}">${escapeHtml(measurement.label)}</label>`;
      }).join('');
      return `<div class="orbea-configurator__measurements"><p class="orbea-configurator__measurement-title">${escapeHtml(title)}</p><div class="orbea-configurator__measurement-list">${items}</div></div>`;
    }

    bikeVariantMarkup() {
      const colorIndex = this.optionIndex(/kleur|color|colour/i);
      const sizeIndex = this.optionIndex(/maat|size/i);
      const currentColor = this.currentColor();
      const currentSize = this.currentSize();
      const profile = this.currentProfile();
      const displayedColor = this.selectionMatchesProfile(profile) ? profile.color : currentColor;
      const colors = colorIndex >= 0 ? this.productOptionValues(colorIndex) : [];
      const sizes = sizeIndex >= 0 ? this.productOptionValues(sizeIndex) : [];
      const colorItems = colors.map((choice, index) => {
        const inputId = `${this.id}-bike-color-${index}`;
        return `<span class="orbea-configurator__bike-color-choice">
          <input class="orbea-configurator__bike-color-input" type="radio" id="${escapeHtml(inputId)}"
            name="${escapeHtml(`${this.id}-bike-color`)}" value="${escapeHtml(choice.value)}"
            data-orbea-bike-color="${colorIndex}" ${choice.value === currentColor ? 'checked' : ''}
            ${choice.disabled ? 'disabled' : ''}>
          <label class="orbea-configurator__bike-color-swatch" for="${escapeHtml(inputId)}" title="${escapeHtml(choice.value)}">
            <span class="orbea-configurator__bike-color-paint" aria-hidden="true"${choice.swatchBackground ? ` style="--orbea-bike-swatch-background:${escapeHtml(choice.swatchBackground)}"` : ''}></span>
            <span class="visually-hidden">${escapeHtml(choice.value)}</span>
          </label>
        </span>`;
      }).join('');
      const sizeItems = sizes.map((choice, index) => {
        const inputId = `${this.id}-bike-size-${index}`;
        return `<input class="orbea-configurator__measurement-input" type="radio" id="${escapeHtml(inputId)}"
          name="${escapeHtml(`${this.id}-bike-size`)}" value="${escapeHtml(choice.value)}"
          data-orbea-bike-size="${sizeIndex}" ${choice.value === currentSize ? 'checked' : ''}
          ${choice.disabled ? 'disabled' : ''}>
          <label class="orbea-configurator__measurement-label" for="${escapeHtml(inputId)}">${escapeHtml(choice.value)}</label>`;
      }).join('');
      return `<div class="orbea-configurator__bike-variants">
        ${colorItems ? `<div class="orbea-configurator__bike-variant">
          <p class="orbea-configurator__measurement-title">Kleur</p>
          <div class="orbea-configurator__bike-color-row">
            <span class="orbea-configurator__bike-color-swatches">${colorItems}</span>
            <span class="orbea-configurator__bike-color-selected" aria-live="polite">${escapeHtml(displayedColor)}</span>
          </div>
        </div>` : ''}
        ${sizeItems ? `<div class="orbea-configurator__bike-variant">
          <p class="orbea-configurator__measurement-title">Maat</p>
          <div class="orbea-configurator__measurement-list">${sizeItems}</div>
        </div>` : ''}
      </div>`;
    }

    render() {
      const size = this.currentSize();
      const groups = (this.config.groups || []).map((group) => {
        const option = this.selectedOption(group);
        const isRotorGroup = this.isRotorGroup(group);
        const compactCrank = this.isCrankGroup(group) && this.canUseCompactCrank(group);
        const optionMarkup = this.isCrankGroup(group)
          ? compactCrank
            ? this.crankPowerMarkup(group)
            : this.orderedOptions(group).map((item, index) => this.optionMarkup(group, item, index)).join('')
          : isRotorGroup ? '' : this.orderedOptions(group).map((item, index) => this.optionMarkup(group, item, index)).join('');
        const crankChainrings = compactCrank ? this.crankChainringMarkup(group) : '';
        const rotorSelectors = isRotorGroup ? this.rotorMarkup(group) : '';
        return `<fieldset class="orbea-configurator__group">
          <legend class="orbea-configurator__legend">${escapeHtml(groupLabel(group))}</legend>
          ${optionMarkup ? `<div class="orbea-configurator__options">${optionMarkup}</div>` : ''}
          ${crankChainrings}
          ${rotorSelectors}
          ${size ? this.measurementMarkup(group, option, size) : ''}
        </fieldset>`;
      }).join('');
      const profile = this.currentProfile();
      const stockIntro = profile
        ? `<div class="orbea-configurator__stock-intro">
            <span class="orbea-configurator__saved-badge orbea-configurator__stock-badge">Winkelvoorraad</span>
            <span class="orbea-configurator__stock-copy"><strong>Deze fiets staat in onze winkel.</strong> De gemonteerde opties zijn vooraf geselecteerd. Wijzig je iets, dan wordt de fiets besteld bij Orbea.</span>
          </div>`
        : '';
      this.content.innerHTML = `${this.bikeVariantMarkup()}${stockIntro}<div class="orbea-configurator__groups">${groups}</div>`;
      this.updateTotals();
      this.layoutImageCrops();
    }

    handleChange(event) {
      const bikeColorInput = event.target.closest('[data-orbea-bike-color]');
      if (bikeColorInput) {
        this.selectProductOption(Number(bikeColorInput.dataset.orbeaBikeColor), bikeColorInput.value);
        return;
      }
      const bikeSizeInput = event.target.closest('[data-orbea-bike-size]');
      if (bikeSizeInput) {
        this.selectProductOption(Number(bikeSizeInput.dataset.orbeaBikeSize), bikeSizeInput.value);
        return;
      }
      const crankPowerInput = event.target.closest('[data-orbea-crank-power]');
      if (crankPowerInput) {
        const group = (this.config.groups || []).find((candidate) => String(candidate.id) === crankPowerInput.dataset.orbeaCrankPower);
        if (!group) return;
        const selected = this.selectedOption(group);
        if (this.setCrankSelection(group, crankPowerInput.value, this.crankChainring(selected))) {
          this.markConfigurationEdited();
          this.render();
          this.applyPageState();
        }
        return;
      }
      const crankChainringInput = event.target.closest('[data-orbea-crank-chainring]');
      if (crankChainringInput) {
        const group = (this.config.groups || []).find((candidate) => String(candidate.id) === crankChainringInput.dataset.orbeaCrankChainring);
        if (!group) return;
        const selected = this.selectedOption(group);
        if (this.setCrankSelection(group, this.crankPowerKey(selected), crankChainringInput.value)) {
          this.markConfigurationEdited();
          this.render();
          this.applyPageState();
        }
        return;
      }
      const rotorFrontInput = event.target.closest('[data-orbea-rotor-front]');
      if (rotorFrontInput) {
        const group = (this.config.groups || []).find((candidate) => String(candidate.id) === rotorFrontInput.dataset.orbeaRotorFront);
        if (!group) return;
        const selected = this.rotorSizes(this.selectedOption(group));
        if (this.setRotorSelection(group, rotorFrontInput.value, selected.rear)) {
          this.markConfigurationEdited();
          this.render();
          this.applyPageState();
        }
        return;
      }
      const rotorRearInput = event.target.closest('[data-orbea-rotor-rear]');
      if (rotorRearInput) {
        const group = (this.config.groups || []).find((candidate) => String(candidate.id) === rotorRearInput.dataset.orbeaRotorRear);
        if (!group) return;
        const selected = this.rotorSizes(this.selectedOption(group));
        if (this.setRotorSelection(group, selected.front, rotorRearInput.value)) {
          this.markConfigurationEdited();
          this.render();
          this.applyPageState();
        }
        return;
      }
      const wheelColorInput = event.target.closest('[data-orbea-wheel-color]');
      if (wheelColorInput) {
        const groupId = wheelColorInput.dataset.orbeaWheelColor;
        const optionId = wheelColorInput.dataset.orbeaWheelOption;
        const group = (this.config.groups || []).find((candidate) => String(candidate.id) === String(groupId));
        this.selectedOptions[groupId] = optionId;
        if (group) this.syncWheelColors(group, optionId, wheelColorInput.value);
        delete this.selectedMeasurements[groupId];
        this.markConfigurationEdited();
        this.render();
        this.applyPageState();
        return;
      }
      const optionInput = event.target.closest('[data-orbea-option]');
      if (optionInput) {
        this.selectedOptions[optionInput.dataset.orbeaOption] = optionInput.value;
        delete this.selectedMeasurements[optionInput.dataset.orbeaOption];
        this.markConfigurationEdited();
        this.render();
        this.applyPageState();
        return;
      }
      const measurementInput = event.target.closest('[data-orbea-measurement]');
      if (measurementInput) {
        this.selectedMeasurements[measurementInput.dataset.orbeaMeasurement] = measurementInput.value;
        this.markConfigurationEdited();
        this.applyPageState();
      }
    }

    updateTotals() {
      if (!this.totalNode || !this.deltaNode) return;
      const profile = this.currentProfile();
      const profileMatch = this.selectionMatchesProfile(profile) ? profile : null;
      const delta = this.selectedDelta();
      this.totalNode.textContent = this.formatMoney(this.effectivePrice(profileMatch));
      this.deltaNode.textContent = profileMatch
        ? 'Exact onze fiets uit winkelvoorraad'
        : delta ? `${this.formatMoney(delta, true)} door componentkeuzes` : 'Standaardconfiguratie';
      this.saveButton.disabled = !this.currentSize();
    }

    updateSavedSummary() {
      this.applyPageState();
    }

    save() {
      this.updateSavedSummary();
      this.close();
    }

    open() {
      this.restoreFocusTo = document.activeElement;
      this.render();
      this.overlay.hidden = false;
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('orbea-configurator-is-open');
      this.closeButton.focus();
    }

    close() {
      if (!this.overlay || this.overlay.hidden) return;
      this.overlay.hidden = true;
      this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('orbea-configurator-is-open');
      if (this.restoreFocusTo instanceof HTMLElement) this.restoreFocusTo.focus();
    }

    handleKeydown(event) {
      if (!this.overlay || this.overlay.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'))
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    positionCroppedImage(image) {
      const crop = String(image.dataset.orbeaImageCrop || '').split(',').map(Number);
      if (crop.length !== 4 || !crop.every(Number.isFinite) || !image.naturalWidth || !image.naturalHeight) return;
      const wrap = image.closest('.orbea-configurator__image-wrap');
      if (!wrap) return;
      const [x, y, width, height] = crop;
      const style = window.getComputedStyle(wrap);
      const innerWidth = wrap.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const innerHeight = wrap.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const scale = Math.min(innerWidth / (image.naturalWidth * width), innerHeight / (image.naturalHeight * height));
      image.style.width = `${image.naturalWidth * scale}px`;
      image.style.height = `${image.naturalHeight * scale}px`;
      image.style.left = `${parseFloat(style.paddingLeft) + (innerWidth - image.naturalWidth * width * scale) / 2 - x * image.naturalWidth * scale}px`;
      image.style.top = `${parseFloat(style.paddingTop) + (innerHeight - image.naturalHeight * height * scale) / 2 - y * image.naturalHeight * scale}px`;
    }

    layoutImageCrops() {
      this.imageCropObserver?.disconnect();
      const componentImages = Array.from(this.content?.querySelectorAll('[data-orbea-component-image]') || []);
      componentImages.forEach((image) => image.addEventListener('error', () => {
        const wrap = image.closest('.orbea-configurator__image-wrap');
        const label = image.closest('.orbea-configurator__option');
        wrap?.remove();
        label?.classList.add('orbea-configurator__option--text-only');
      }, { once: true }));
      const images = Array.from(this.content?.querySelectorAll('[data-orbea-image-crop]') || []);
      images.forEach((image) => image.complete ? this.positionCroppedImage(image) : image.addEventListener('load', () => this.positionCroppedImage(image), { once: true }));
      if (typeof ResizeObserver !== 'function' || !images.length) return;
      this.imageCropObserver = new ResizeObserver((entries) => entries.forEach((entry) => {
        const image = entry.target.querySelector('[data-orbea-image-crop]');
        if (image) this.positionCroppedImage(image);
      }));
      images.forEach((image) => this.imageCropObserver.observe(image.closest('.orbea-configurator__image-wrap')));
    }
  }

  customElements.define('orbea-configurator', OrbeaConfigurator);
})();
